import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ParseVendorQuoteResponse,
  type CommitVendorQuoteResponse,
  type Currency,
  type ConditionCode,
  type CertType,
  type Incoterms2020,
} from '@/lib/api';
import { useModalDismiss } from '@/lib/use-modal-dismiss';

type Tab = 'paste' | 'pdf';

interface Props {
  open: boolean;
  onClose: () => void;
  // The RFQ + line context we're parsing into. Both optional — modal works
  // standalone too (parser will surface candidate lines from across all RFQs).
  rfqId?: string;
  hintRfqLineItemId?: string;
}

const CONDITIONS: ConditionCode[] = ['NE', 'NS', 'FN', 'OH', 'SV', 'AR', 'RP'];
const CERTS: CertType[] = ['faa_8130_3', 'easa_form_1', 'dual_release', 'coc', 'none'];
const INCOTERMS: Incoterms2020[] = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'];
const CURRENCIES: Currency[] = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'AED', 'SGD'];

// Form state mirrors the commit body, with nullable fields as undefined for
// uncontrolled inputs.
interface FormState {
  rfqLineItemPublicId: string;
  vendorNameRaw: string;
  vendorContactName: string;
  vendorContactEmail: string;
  unitPriceCents: number;
  currency: Currency;
  qtyOffered: number;
  conditionOffered?: ConditionCode;
  certOffered?: CertType;
  leadTimeDays?: number;
  // Exchange terms. Kept as typed text so a half-typed amount is not mangled.
  isExchange: boolean;
  coreDollars: string;
  coreDueDate: string;
  coreReturnDays: string;
  incoterms?: Incoterms2020;
  fobLocation: string;
  validUntil: string;
  eccn: string;
  htsCode: string;
  countryOfOrigin: string;
  hazmatClass: string;
  notes: string;
}

/** The exchange part of the commit body. Nothing is sent for an outright
 *  quote, and a blank core amount sends no amount rather than zero. */
export function exchangeCommitFields(f: Pick<FormState, 'isExchange' | 'coreDollars' | 'coreDueDate' | 'coreReturnDays'>): {
  isExchange?: boolean;
  coreChargeCents?: number;
  coreDueAt?: string;
  coreReturnDays?: number;
} {
  if (!f.isExchange) return {};
  const core = parseFloat(f.coreDollars.replace(/,/g, ''));
  const days = parseInt(f.coreReturnDays, 10);
  return {
    isExchange: true,
    ...(Number.isFinite(core) && core > 0 ? { coreChargeCents: Math.round(core * 100) } : {}),
    ...(/^\d{4}-\d{2}-\d{2}$/.test(f.coreDueDate) ? { coreDueAt: `${f.coreDueDate}T23:59:59.000Z` } : {}),
    ...(Number.isFinite(days) && days > 0 && days <= 365 ? { coreReturnDays: days } : {}),
  };
}

function emptyForm(): FormState {
  return {
    rfqLineItemPublicId: '',
    vendorNameRaw: '',
    vendorContactName: '',
    vendorContactEmail: '',
    unitPriceCents: 0,
    currency: 'USD',
    qtyOffered: 1,
    isExchange: false,
    coreDollars: '',
    coreDueDate: '',
    coreReturnDays: '',
    fobLocation: '',
    validUntil: '',
    eccn: '',
    htsCode: '',
    countryOfOrigin: '',
    hazmatClass: '',
    notes: '',
  };
}

export function ParseVendorQuoteModal({ open, onClose, rfqId, hintRfqLineItemId }: Props) {
  const dismiss = useModalDismiss(onClose);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('paste');
  const [rawText, setRawText] = useState('');
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfName, setPdfName] = useState<string | null>(null);
  const [parsedResp, setParsedResp] = useState<ParseVendorQuoteResponse | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [postCommitWarnings, setPostCommitWarnings] = useState<string[] | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset state when modal closes so reopening is clean
      setRawText('');
      setPdfBase64(null);
      setPdfName(null);
      setParsedResp(null);
      setForm(emptyForm());
      setTab('paste');
      setPostCommitWarnings(null);
    }
  }, [open]);

  function finishAndClose() {
    qc.invalidateQueries({ queryKey: ['vendor-quotes'] });
    qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
    qc.invalidateQueries({ queryKey: ['rfqs'] });
    onClose();
  }

  const parseMutation = useMutation({
    mutationFn: async () => {
      const body = tab === 'pdf'
        ? { source: 'pdf' as const, pdfBase64, fileName: pdfName, hintRfqLineItemPublicId: hintRfqLineItemId }
        : { source: 'paste' as const, rawText, hintRfqLineItemPublicId: hintRfqLineItemId };
      return api<ParseVendorQuoteResponse>('/api/vendor-quotes/parse', {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    // PDF parsing is the longest call in the app (~15-45s) and is the most
    // exposed to transient gateway 502/503/504s (edge proxy timing out or the
    // server restarting on deploy). Auto-retry those so a blip self-heals
    // instead of dumping an error on the broker. Don't retry real 4xx/parse
    // failures — those won't get better on retry.
    retry: (failureCount: number, error: unknown) => {
      const status = (error as { status?: number })?.status;
      const transient = status === 502 || status === 503 || status === 504;
      return transient && failureCount < 2;
    },
    retryDelay: (attempt: number) => Math.min(2000 * 2 ** attempt, 8000),
    onSuccess: (data) => {
      setParsedResp(data);
      const p = data.parsed;
      const initialLineId = data.candidateLines[0]?.id ?? hintRfqLineItemId ?? '';
      setForm({
        rfqLineItemPublicId: initialLineId,
        vendorNameRaw: p.vendor.name ?? '',
        vendorContactName: p.vendor.contact_name ?? '',
        vendorContactEmail: p.vendor.contact_email ?? '',
        unitPriceCents: p.unitPriceCents,
        currency: p.currency,
        qtyOffered: p.qtyOffered,
        conditionOffered: p.conditionOffered ?? undefined,
        certOffered: p.certOffered ?? undefined,
        leadTimeDays: p.leadTimeDays ?? undefined,
        isExchange: Boolean(p.isExchange),
        coreDollars: p.coreChargeCents != null ? (p.coreChargeCents / 100).toFixed(2) : '',
        coreDueDate: p.coreDue ? p.coreDue.slice(0, 10) : '',
        coreReturnDays: p.coreReturnDays != null ? String(p.coreReturnDays) : '',
        incoterms: p.incoterms ?? undefined,
        fobLocation: p.fobLocation ?? '',
        validUntil: p.validUntil ?? '',
        eccn: p.eccn ?? '',
        htsCode: p.htsCode ?? '',
        countryOfOrigin: p.countryOfOrigin ?? '',
        hazmatClass: p.hazmatClass ?? '',
        notes: p.notes ?? '',
      });
    },
  });

  const commitMutation = useMutation({
    mutationFn: async () => {
      const source =
        parsedResp == null ? 'manual'
        : parsedResp.parser.inputType === 'pdf' ? 'ai_parsed_pdf'
        : 'ai_parsed_email';

      const body = {
        rfqLineItemPublicId: form.rfqLineItemPublicId,
        vendorNameRaw: form.vendorNameRaw,
        vendorContactName: form.vendorContactName || undefined,
        vendorContactEmail: form.vendorContactEmail || undefined,
        unitPriceCents: form.unitPriceCents,
        currency: form.currency,
        qtyOffered: form.qtyOffered,
        conditionOffered: form.conditionOffered,
        certOffered: form.certOffered,
        leadTimeDays: form.leadTimeDays,
        // The parser has always extracted these; this screen used to drop them
        // on commit, so an exchange parsed here was saved as an outright sale
        // and its return window was lost for good.
        ...exchangeCommitFields(form),
        incoterms: form.incoterms,
        fobLocation: form.fobLocation || undefined,
        validUntil: form.validUntil ? new Date(form.validUntil).toISOString() : undefined,
        eccn: form.eccn || undefined,
        htsCode: form.htsCode || undefined,
        countryOfOrigin: form.countryOfOrigin || undefined,
        hazmatClass: form.hazmatClass || undefined,
        source,
        parserConfidence: parsedResp?.parser.confidence,
        parserModel: parsedResp?.parser.model,
        rawSourceBlob: tab === 'paste' ? rawText : undefined,
        pdfBase64: tab === 'pdf' ? pdfBase64 ?? undefined : undefined,
        pdfFileName: tab === 'pdf' ? pdfName ?? undefined : undefined,
        notes: form.notes || undefined,
      };

      return api<CommitVendorQuoteResponse>('/api/vendor-quotes', {
        method: 'POST',
        tokenGetter: () => getToken(),
        idempotencyKey: crypto.randomUUID(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: (data) => {
      if (data.warnings && data.warnings.length > 0) {
        // Hold the modal open; user must acknowledge before close so they
        // know about silent issues (e.g. PDF wasn't archived to R2).
        setPostCommitWarnings(data.warnings);
        // Still invalidate now so the new quote appears under the modal.
        qc.invalidateQueries({ queryKey: ['vendor-quotes'] });
        qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
        qc.invalidateQueries({ queryKey: ['rfqs'] });
        return;
      }
      finishAndClose();
    },
  });

  function onPdfSelect(file: File | null) {
    if (!file) {
      setPdfBase64(null);
      setPdfName(null);
      return;
    }
    if (file.type !== 'application/pdf') {
      alert('Please select a PDF file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('PDF too large (max 5MB).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip the "data:application/pdf;base64," prefix
      const base64 = result.split(',', 2)[1] ?? '';
      setPdfBase64(base64);
      setPdfName(file.name);
    };
    reader.readAsDataURL(file);
  }

  if (!open) return null;

  const canParse = (tab === 'paste' && rawText.trim().length >= 20) || (tab === 'pdf' && pdfBase64);
  const canCommit =
    !!parsedResp &&
    form.rfqLineItemPublicId &&
    form.vendorNameRaw.trim().length > 0 &&
    form.qtyOffered > 0 &&
    form.unitPriceCents >= 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      {...dismiss}
    >
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[92dvh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="font-semibold">Parse vendor quote</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Paste a vendor email or upload their PDF. Claude extracts price, condition, lead time, cert, FOB, validity. Review, attach to a line, commit.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm" aria-label="Close">✕</button>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4">
          {postCommitWarnings ? (
            <div className="space-y-3">
              <div className="text-sm">
                Vendor quote saved. A few things worth noting:
              </div>
              <ul className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md p-3 text-sm space-y-1">
                {postCommitWarnings.map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
              <div className="text-xs text-muted-foreground">
                The quote is saved either way — these are non-blocking. Configure R2 in <span className="font-mono">.env</span> to archive PDFs going forward.
              </div>
            </div>
          ) : !parsedResp ? (
            <>
              <div className="flex gap-1 mb-3 border-b border-border">
                <button
                  onClick={() => setTab('paste')}
                  className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'paste' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`}
                >
                  Paste text
                </button>
                <button
                  onClick={() => setTab('pdf')}
                  className={`px-3 py-1.5 text-sm border-b-2 -mb-px ${tab === 'pdf' ? 'border-primary font-medium' : 'border-transparent text-muted-foreground'}`}
                >
                  Upload PDF
                </button>
              </div>

              {tab === 'paste' ? (
                <textarea
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  placeholder={'Hi Austin,\n\nThanks for your RFQ on PN 285W0024-1M.\n\nWe have 2 ea NEW in stock, ready to ship.\nUnit price: $4,250.00 USD\nLead time: ex-stock (1-2 days)\nFAA 8130-3 included.\nFOB Miami, FL.\nQuote valid 30 days.\n\nLet me know if you want to proceed.\n\nThanks,\nJuan at Aerotechnic Middle East'}
                  className="w-full h-72 px-3 py-2 text-sm font-mono bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40"
                  autoFocus
                />
              ) : (
                <div className="border border-dashed border-border rounded-md p-6 text-center">
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(e) => onPdfSelect(e.target.files?.[0] ?? null)}
                    className="block mx-auto text-sm"
                  />
                  {pdfName && (
                    <div className="mt-3 text-sm text-muted-foreground">
                      Loaded <span className="font-mono">{pdfName}</span>
                    </div>
                  )}
                  <p className="mt-3 text-xs text-muted-foreground">
                    Max 5MB. Native PDF support — no OCR step needed for digital vendor quotes; scanned PDFs are also handled via vision.
                  </p>
                </div>
              )}

              {parseMutation.isError && (
                <div className="mt-3 text-sm text-destructive">
                  {(parseMutation.error as { message?: string })?.message ?? 'Parse failed'}
                </div>
              )}
            </>
          ) : (
            <ReviewForm
              parsed={parsedResp}
              form={form}
              setForm={setForm}
              error={(commitMutation.error as { message?: string } | null)?.message ?? null}
            />
          )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-border">
          {parsedResp && !postCommitWarnings && (
            <button
              onClick={() => setParsedResp(null)}
              className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted"
            >
              ← Re-parse
            </button>
          )}
          <div className="flex items-center gap-2 ml-auto">
            {postCommitWarnings ? (
              <button
                onClick={finishAndClose}
                className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md"
              >
                Got it
              </button>
            ) : !parsedResp ? (
              <>
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
                  Cancel
                </button>
                <button
                  onClick={() => parseMutation.mutate()}
                  disabled={!canParse || parseMutation.isPending}
                  className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {parseMutation.isPending ? 'Parsing…' : 'Parse'}
                </button>
              </>
            ) : (
              <>
                <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-md text-muted-foreground hover:bg-muted">
                  Cancel
                </button>
                <button
                  onClick={() => commitMutation.mutate()}
                  disabled={!canCommit || commitMutation.isPending}
                  className="px-4 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-md disabled:opacity-50"
                >
                  {commitMutation.isPending ? 'Saving…' : 'Save quote'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ReviewForm({
  parsed,
  form,
  setForm,
  error,
}: {
  parsed: ParseVendorQuoteResponse;
  form: FormState;
  setForm: (f: FormState) => void;
  error: string | null;
}) {
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm({ ...form, [k]: v });
  const dollars = (form.unitPriceCents / 100).toFixed(2);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 text-xs">
        <span className="px-2 py-0.5 bg-accent text-accent-foreground rounded">
          Confidence {(parsed.parser.confidence * 100).toFixed(0)}%
        </span>
        <span className="text-muted-foreground">{parsed.parser.model}</span>
        <span className="text-muted-foreground">{parsed.parser.durationMs}ms</span>
        <span className="text-muted-foreground">{parsed.parser.inputType}</span>
      </div>

      {parsed.parser.warnings.length > 0 && (
        <div className="border border-border rounded-md p-3 bg-amber-50 text-amber-900 text-sm">
          <div className="text-xs font-medium uppercase tracking-wide mb-1">Parser warnings</div>
          <ul className="space-y-0.5">{parsed.parser.warnings.map((w, i) => <li key={i}>· {w}</li>)}</ul>
        </div>
      )}

      <Field label="Attach to RFQ line">
        <select
          value={form.rfqLineItemPublicId}
          onChange={(e) => update('rfqLineItemPublicId', e.target.value)}
          className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
        >
          <option value="" disabled>Select a line…</option>
          {parsed.candidateLines.map((c) => (
            <option key={c.id} value={c.id}>
              {c.rfqRef} · #{c.lineNumber} · {c.partNumber}{c.altPartNumber ? ` / ${c.altPartNumber}` : ''} · qty {c.qty} · {c.stage}
              {c.rfqIsAog ? ' · AOG' : ''}
            </option>
          ))}
        </select>
        {parsed.candidateLines.length === 0 && (
          <div className="text-xs text-destructive mt-1">
            No matching lines found for PN {parsed.parsed.partNumber}. Open an RFQ first or check the PN.
          </div>
        )}
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Vendor name">
          <input value={form.vendorNameRaw} onChange={(e) => update('vendorNameRaw', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Vendor contact email">
          <input value={form.vendorContactEmail} onChange={(e) => update('vendorContactEmail', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Vendor contact name">
          <input value={form.vendorContactName} onChange={(e) => update('vendorContactName', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Currency">
          <select value={form.currency} onChange={(e) => update('currency', e.target.value as Currency)} className={inputCls}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={`Unit price (${form.currency})`}>
          <input
            type="number"
            min={0}
            step="0.01"
            value={dollars}
            onChange={(e) => update('unitPriceCents', Math.round(Number(e.target.value) * 100))}
            className={inputCls}
          />
        </Field>
        <Field label="Qty offered">
          <input
            type="number"
            min={1}
            value={form.qtyOffered}
            onChange={(e) => update('qtyOffered', Number(e.target.value))}
            className={inputCls}
          />
        </Field>
        <Field label="Condition offered">
          <select
            value={form.conditionOffered ?? ''}
            onChange={(e) => update('conditionOffered', (e.target.value || undefined) as ConditionCode | undefined)}
            className={inputCls}
          >
            <option value="">—</option>
            {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Cert offered">
          <select
            value={form.certOffered ?? ''}
            onChange={(e) => update('certOffered', (e.target.value || undefined) as CertType | undefined)}
            className={inputCls}
          >
            <option value="">—</option>
            {CERTS.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
          </select>
        </Field>
        <Field label="Lead time (days)">
          <input
            type="number"
            min={0}
            value={form.leadTimeDays ?? ''}
            onChange={(e) => update('leadTimeDays', e.target.value ? Number(e.target.value) : undefined)}
            className={inputCls}
          />
        </Field>
        <Field label="Incoterms">
          <select
            value={form.incoterms ?? ''}
            onChange={(e) => update('incoterms', (e.target.value || undefined) as Incoterms2020 | undefined)}
            className={inputCls}
          >
            <option value="">—</option>
            {INCOTERMS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="FOB location">
          <input value={form.fobLocation} onChange={(e) => update('fobLocation', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Valid until">
          <input
            type="date"
            value={form.validUntil ? form.validUntil.slice(0, 10) : ''}
            onChange={(e) => update('validUntil', e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="ECCN">
          <input value={form.eccn} onChange={(e) => update('eccn', e.target.value)} className={inputCls} />
        </Field>
        <Field label="HTS code">
          <input value={form.htsCode} onChange={(e) => update('htsCode', e.target.value)} className={inputCls} />
        </Field>
        <Field label="Country of origin (ISO-2)">
          <input value={form.countryOfOrigin} maxLength={2} onChange={(e) => update('countryOfOrigin', e.target.value.toUpperCase())} className={inputCls} />
        </Field>
        <Field label="Hazmat class">
          <input value={form.hazmatClass} onChange={(e) => update('hazmatClass', e.target.value)} className={inputCls} />
        </Field>
      </div>

      <div className="rounded border border-border p-3 space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <input type="checkbox" checked={form.isExchange} onChange={(e) => update('isExchange', e.target.checked)} />
          Exchange (core return required)
        </label>
        {form.isExchange && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label={`Core charge (${form.currency})`}>
              <input
                inputMode="decimal"
                value={form.coreDollars}
                onChange={(e) => update('coreDollars', e.target.value)}
                placeholder="e.g. 35000.00"
                className={`${inputCls} font-mono`}
              />
            </Field>
            <Field label="Core due back by">
              <input type="date" value={form.coreDueDate} onChange={(e) => update('coreDueDate', e.target.value)} className={inputCls} />
            </Field>
            <Field label="…or within (days)">
              <input
                type="number"
                min={1}
                max={365}
                value={form.coreReturnDays}
                onChange={(e) => update('coreReturnDays', e.target.value)}
                className={inputCls}
              />
            </Field>
          </div>
        )}
      </div>

      <Field label="Notes">
        <textarea
          value={form.notes}
          onChange={(e) => update('notes', e.target.value)}
          className={`${inputCls} h-20 font-mono text-xs`}
        />
      </Field>

      {error && <div className="text-sm text-destructive">{error}</div>}
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 text-sm bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-primary/40';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</div>
      {children}
    </label>
  );
}
